import { type Stop, createStop } from 'core/domain/Stop'

import { type EquipmentType } from '../../core/domain/EquipmentType'
import { type EquipmentSize } from '../domain/EquipmentSize'
import { type ExtraFields } from './ExtraFields'
import { type LanePreference } from './LanePreferences'
import { type LaneProposal } from './LaneProposal'
import { type MileageSource } from './MileageSource'
import { type Mode } from './Mode'

export type Lane = {
  id: number
  additionalStops: Array<Stop>
  destCity?: string
  destCountry?: string
  destLatitude?: string
  destLongitude?: string
  destState?: string
  destTimezone?: string
  destZip?: string
  equipmentType: EquipmentType
  equipmentSize?: EquipmentSize
  extraFields?: ExtraFields
  laneId: string
  laneProposal?: LaneProposal
  laneProposals?: LaneProposal[]
  preferences: LanePreference[]
  distance: number
  mileageSource: MileageSource
  notes?: string
  originCity?: string
  originCountry?: string
  originLatitude?: string
  originLongitude?: string
  originState?: string
  originTimezone?: string
  originZip?: string
  previousProposal?: number
  stops: Array<Stop>
  targetRange?: string
  volume: number
}

export type LanesPerModePreference = Record<Mode, Lane[]>

export function getExtraFields(lane: Lane): Array<string> {
  return Object.keys(lane.extraFields ?? {}).filter(key => Boolean(lane.extraFields![key]))
}

export function getInitialFlatRate(isMultiCurrency: boolean, laneProposal?: LaneProposal) {
  if (!laneProposal) return undefined
  return Number(isMultiCurrency ? laneProposal.convertedFlatRate : laneProposal.flatRate)
}

export function getInitialPerDistanceRate(isMultiCurrency: boolean, laneProposal?: LaneProposal) {
  if (!laneProposal) return undefined
  return Number(
    isMultiCurrency ? laneProposal.convertedPerDistanceRate : laneProposal.perDistanceRate
  )
}

export function getStops(lane: Lane): Stop[] {
  return [mapOriginToStop(lane), ...lane.additionalStops, mapDestinationToStop(lane)]
}

export function getStopsCount(lane: Lane) {
  return 2 + lane.additionalStops.length
}

export function hasExtraFields(lane: Lane): boolean {
  return Object.values(lane.extraFields ?? {}).filter(Boolean).length > 0
}

export function hasPreferencesExtraFields(lane: Lane): boolean {
  if (!lane.preferences) return false

  return lane.preferences.some(preference => Object.values(preference.extraFields ?? {}).length > 0)
}

export function hasLaneDetails(lane: Lane): boolean {
  const extraFields = hasPreferencesExtraFields(lane)

  return extraFields || Boolean(lane.notes)
}

export function isLaneAwardedAccepted({ laneProposal }: Lane) {
  return laneProposal?.awardConfirmation === 'accepted'
}

export function isLaneAwardedConfirmed({ laneProposal }: Lane) {
  return Boolean(laneProposal?.awardConfirmation)
}

export function mapDestinationToStop(lane: Lane): Stop {
  return createStop(lane.destCity!, lane.destState!, {
    country: lane.destCountry,
    latitude: lane.destLatitude,
    longitude: lane.destLongitude,
    zipCode: lane.destZip,
  })
}

export function mapOriginToStop(lane: Lane): Stop {
  return createStop(lane.originCity!, lane.originState!, {
    country: lane.originCountry,
    latitude: lane.originLatitude,
    longitude: lane.originLongitude,
    zipCode: lane.originZip,
  })
}

function getLaneWithOnePreference(lane: Lane, preferenceIndex: number) {
  const { preferences } = lane

  return { ...lane, preferences: [preferences![preferenceIndex]] }
}

export function getLanesPerModePreference(lanes: Lane[]) {
  let modePreferences = {} as LanesPerModePreference

  lanes.forEach(lane => {
    lane.preferences?.forEach((preference, preferenceIndex) => {
      if (preference.mode in modePreferences) {
        modePreferences[preference.mode].push(getLaneWithOnePreference(lane, preferenceIndex))
      } else {
        modePreferences[preference.mode] = [getLaneWithOnePreference(lane, preferenceIndex)]
      }
    })
  })

  return modePreferences
}

function getPreferenceFromProposal(lane: Lane, preferenceId: string | undefined) {
  return lane.preferences?.find(preference => preference.id === preferenceId) as LanePreference
}

/**
 * This method handles the scenario when a given carrier
 * is awarded multiple times for the same lane through different preferences.
 * We call this scenario "Split Primary", and with the introduction of multimode RFPs,
 * the same carrier can be in the "both sides" of the Split Primary with different modes/equipment types.
 *
 * In this case we need to duplicate the lane for each preference awarded.
 * So the carrier can accept or reject each proposal separately.
 *
 * @param lanes
 * @returns lanes duplicated by proposals for the same preference awarded
 */
export function getLanePerProposalPreference(lanes: Lane[]) {
  let proposalPreferences: Lane[] = []

  lanes.forEach(lane => {
    lane.laneProposals?.forEach(laneProposal => {
      proposalPreferences.push({
        ...lane,
        laneProposal,
        preferences: [getPreferenceFromProposal(lane, laneProposal.lanePreferenceId)],
      })
    })
  })

  return proposalPreferences
}
